import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trash2, Check, Gift } from "lucide-react";
import { api } from "@/lib/api";
import { useNoIndex } from "@/lib/useNoIndex";
import { t } from "@/lib/i18n";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";

export default function RemovalPage() {
  useNoIndex();
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);

  useEffect(() => {
    api
      .get(`/answers/removal/${token}`)
      .then(({ data }) => setInfo(data))
      .catch(() => setInfo({ found: false }))
      .finally(() => setLoading(false));
  }, [token]);

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/answers/removal/${token}`);
      setDeleted(true);
      toast.success(t("removal.success"));
    } catch (err) {
      toast.error(t("common.deleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-[#7A6B63]">{t("common.loading")}</div>;

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7]">
      <div className="flex-1 grid place-items-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-lg w-full text-center"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-[#C86D51] mb-6">
          <Gift className="w-6 h-6" />
          <span className="text-sm font-semibold tracking-wide uppercase">Portwrite</span>
        </Link>

        {!info.found || deleted ? (
          <div className="rounded-3xl border border-[#E8DEC2] bg-white p-8 shadow-sm" data-testid="removal-result">
            <div className="w-14 h-14 rounded-full bg-[#EAF1EA] grid place-items-center mx-auto mb-5">
              <Check className="w-7 h-7 text-[#3f6047]" />
            </div>
            <h1 className="font-serif text-3xl text-[#2C221E]">
              {deleted ? t("removal.deleted") : t("removal.notFound")}
            </h1>
            <p className="mt-3 text-[#7A6B63]">
              {deleted ? t("removal.deletedBody") : t("removal.notFoundBody")}
            </p>
          </div>
        ) : (
          <div className="rounded-3xl border border-[#E8DEC2] bg-white p-8 shadow-sm">
            <h1 className="font-serif text-3xl text-[#2C221E]">{t("removal.title")}</h1>
            <p className="mt-3 text-[#4A3E39] leading-relaxed">
              {t("removal.confirmBody", { name: info.person_name })}
            </p>
            <Button
              onClick={remove}
              disabled={deleting}
              className="mt-6 w-full h-12 bg-[#B84A39] hover:bg-[#9d3e30] text-white rounded-xl"
              data-testid="confirm-removal-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deleting ? t("removal.deleting") : t("removal.button")}
            </Button>
          </div>
        )}
      </motion.div>
      </div>
      <Footer />
    </div>
  );
}
